<template>
  <div class="tags-selector">
    <v-skeleton-loader
      v-if="!tags || !showTags"
      class="tags-selector__loader"
      type="chip@4"
      indeterminate
      color="primary"
    ></v-skeleton-loader>
    <template v-else-if="showTags">
      <ReviewBoosterTagItem
        v-for="tag in tags"
        v-model="tagsState[tag.id]"
        :key="tag.id"
        :toggle-enabled="!tagsState[tag.id].isBlocked"
        :tag="tag"
        :max-characters="15"
        @change="onTagToggle(tag.id, $event)"
      />
    </template>
  </div>
</template>

<script>
import ReviewBoosterTagItem from "./ReviewBoosterTagItem";
import reviewBoosterService from "@/services/review-booster";

const SENTIMENT_SOURCE = {
  MANUAL: 1,
  AUTO: 2
};

export default {
  name: "ReviewTagsSelector",
  components: {
    ReviewBoosterTagItem
  },
  props: {
    tags: {
      type: Array
    },
    activeTags: {
      type: Array,
      required: true
    },
    showTags: {
      type: Boolean,
      default: true
    },
    reviewId: {
      type: Number,
      required: true
    },
    calculateHeight: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      tagsState: {}
    };
  },

  methods: {
    setTagsState(tags) {
      this.tagsState = tags.reduce((obj, tag) => {
        return {
          ...obj,
          [tag.id]: this.getTagState(tag)
        };
      }, {});
    },

    getTagState(tag) {
      const localTag = this.activeTags.find(i => i.review_category === tag.id);
      return {
        isActive: !!localTag,
        sentiment: localTag && localTag.sentiment,
        isBlocked: localTag && localTag.isBlocked,
        isAutoAssigned:
          localTag && localTag.sentiment_source === SENTIMENT_SOURCE.AUTO
      };
    },
    async onTagToggle(categoryId, { sentiment, isActive }) {
      if (!this.tagsState[categoryId].isActive) {
        await this.assignCategory(categoryId, sentiment);
      } else {
        if (!isActive) {
          await this.unassignCategory(categoryId);
          return;
        }
        await this.updateCategory(categoryId, sentiment);
      }
    },

    async assignCategory(categoryId, sentiment) {
      await reviewBoosterService.assignCategory(this.reviewId, {
        review_category: categoryId,
        sentiment
      });
      this.tagsState[categoryId].isActive = true;
      this.tagsState[categoryId].sentiment = sentiment;
    },

    async updateCategory(categoryId, sentiment) {
      await reviewBoosterService.updateCategory(this.reviewId, categoryId, {
        sentiment
      });
      this.tagsState[categoryId].sentiment = sentiment;
    },
    async unassignCategory(categoryId) {
      await reviewBoosterService.unassignCategory(this.reviewId, categoryId);
      this.tagsState[categoryId].isActive = false;
      this.tagsState[categoryId].sentiment = undefined;
    }
  },
  watch: {
    tags: {
      async handler(currentTags) {
        if (!currentTags) return;
        this.setTagsState(currentTags);
        if (this.calculateHeight) {
          await this.$nextTick();
          this.$emit("tags-generated", this.$el.offsetHeight);
          return;
        }
        this.$emit("tags-generated");
      },
      immediate: true
    }
  }
};
</script>

<style lang="scss" scoped>
.tags-selector {
  display: flex;
  gap: $spacing * 0.75;
  flex-wrap: wrap;
  margin-top: $spacing * 4;
  align-items: flex-start;

  &__loader {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing;

    ::v-deep .v-skeleton-loader__chip {
      height: 46px;
      border-radius: 44px;
      background: rgba(0, 0, 0, 0.09);
    }
  }
}
</style>
